/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { RadixSelectControl } from "../../../ui/radix-select";
import { useStoreDispatch } from "../../../../store/hooks";
import { setOpenCustomDailog } from "../../../../store/helpers/helperReducers";
import { IltepeerInterfaces } from "../../../../store/api/deviceConfig/types";
import { useLteX2ConnectionData } from "./hooks";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { Label } from "../../../ui/label";

interface IAddEditLteX2ConnectionProps {
  selectedLteX2Connection?: IltepeerInterfaces;
  modelOpenMode?: string;
}

const AddEditLteX2Connection: React.FC<IAddEditLteX2ConnectionProps> = ({ modelOpenMode = "Add", selectedLteX2Connection }) => {
  const dispatch = useStoreDispatch();
  const { lteX2ConnectionData, setLteX2ConnectionData, isAddNewLteX2Connection, submitHandler, onblureHandler, errMsg, setErrMsg } =
    useLteX2ConnectionData(selectedLteX2Connection);
  const handleChange = (name: string, value: string) => {
    setLteX2ConnectionData({ ...lteX2ConnectionData!, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setOpenCustomDailog(false));
  };

  const isEnableSave =
    !!lteX2ConnectionData?.index && !!lteX2ConnectionData?.name && !(Object.values(errMsg).filter((item) => !!item && item).length > 0);

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button disabled={!isEnableSave} onClick={submitHandler}>
        Save
      </Button>
    </>
  );

  return (
    <Sheet
      open={true}
      onClose={handleClose}
      title={`${modelOpenMode} LTE X2 Connection Peer`}
      footer={footer}
      width="520px"
    >
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Index */}
          <InputField
            label="Index"
            name="index"
            type="number"
            value={lteX2ConnectionData?.index ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            disabled={!isAddNewLteX2Connection}
            min={1}
            max={16}
            placeholder="Enter index"
            required
            autoFocus={true}
          />

          {/* Enable */}
          <div className="space-y-2">
            <Label htmlFor="lte-x2-enable">Enable</Label>
            <RadixSelectControl
              options={[
                { value: "true", label: "true" },
                { value: "false", label: "false" },
              ]}
              value={lteX2ConnectionData?.enable || "false"}
              onValueChange={(value) => {
                handleChange("enable", value);
              }}
              placeholder="Select"
            />
          </div>

          {/* Name */}
          <InputField
            label="Name"
            name="name"
            value={lteX2ConnectionData?.name ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter name"
            required
          />

          {/* Address */}
          <InputField
            label="Address"
            name="address"
            value={lteX2ConnectionData?.address ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter address"
            required
          />

          {/* PlmnId */}
          <InputField
            label="PlmnId"
            name="plmnId"
            type="number"
            value={lteX2ConnectionData?.plmnId ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter PLMN id"
            required
          />

          {/* Port */}
          <InputField
            label="Port"
            name="port"
            type="number"
            value={lteX2ConnectionData?.port ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            min={1}
            max={65535}
            placeholder="Enter port"
            required
          />

          {/* Tac */}
          <InputField
            label="Tac"
            name="tac"
            type="number"
            value={lteX2ConnectionData?.tac ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter Tac"
            required
          />

          {/* Type */}
          <div className="space-y-2">
            <Label htmlFor="lte-x2-type">Type</Label>
            <RadixSelectControl
              options={[
                { value: "HeNB", label: "HeNB" },
                { value: "Macro", label: "Macro" },
              ]}
              value={lteX2ConnectionData?.type || "HeNB"}
              onValueChange={(value) => {
                handleChange("type", value);
              }}
              placeholder="Select type"
            />
          </div>

          {/* EnodeBID */}
          <InputField
            label="EnodeBID"
            name="eNodeBID"
            type="number"
            value={lteX2ConnectionData?.eNodeBID ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter EnodeBID"
            required
          />

          {/* GnodeBID */}
          <InputField
            label="GnodeBID"
            name="gNodeBID"
            type="number"
            value={lteX2ConnectionData?.gNodeBID ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter GnodeBID"
            required
          />

          {/* ENDCX2 */}
          <div className="space-y-2">
            <Label htmlFor="lte-x2-isENDCX2">ENDCX2</Label>
            <RadixSelectControl
              options={[
                { value: "true", label: "true" },
                { value: "false", label: "false" },
              ]}
              value={lteX2ConnectionData?.isENDCX2 || "false"}
              onValueChange={(value) => {
                handleChange("isENDCX2", value);
              }}
              placeholder="Select"
            />
          </div>
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditLteX2Connection;
